import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CircleDollarSign, Copy, Download, ExternalLink, Eye, MoreHorizontal } from 'lucide-react'
import { toast } from 'sonner'

import { financeApi } from '@/services/finance'
import { Button } from '@/components/ui/button'
import { JobTitleLink, formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { formatCurrency, formatDate } from '@/lib/utils'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { getLocationReturnPath, resolveReturnToPath, RETURN_TO_PARAM, withReturnTo } from '@/lib/return-navigation'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { getErrorMessage } from '@/lib/api'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AlertTriangle } from 'lucide-react'
import { ComplaintsPanel } from '@/components/jobs/job-complaints-panel'

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const extractInvoicePaymentToken = (paymentUrl?: string | null) => {
  const rawUrl = String(paymentUrl ?? '').trim()
  if (!rawUrl) return ''

  try {
    const parsedUrl = new URL(rawUrl, window.location.origin)
    const invoicePayPrefix = '/invoice/pay/'
    if (parsedUrl.pathname.startsWith(invoicePayPrefix)) {
      return decodeURIComponent(parsedUrl.pathname.slice(invoicePayPrefix.length))
    }

    const legacyToken = parsedUrl.searchParams.get('token')
    if (legacyToken) return legacyToken
  } catch {
    // Fall through to a conservative string parse for malformed/relative URLs.
  }

  const invoicePayMatch = rawUrl.match(/\/invoice\/pay\/([^?#]+)/)
  if (invoicePayMatch?.[1]) {
    return decodeURIComponent(invoicePayMatch[1])
  }

  const legacyQueryMatch = rawUrl.match(/[?&]token=([^&]+)/)
  return legacyQueryMatch?.[1] ? decodeURIComponent(legacyQueryMatch[1]) : ''
}

const buildCurrentPortalInvoicePaymentUrl = (
  paymentUrl: string | null | undefined,
  serviceInstanceId?: string | null,
) => {
  const token = extractInvoicePaymentToken(paymentUrl)
  if (!token) return ''

  const params = new URLSearchParams()
  if (serviceInstanceId) {
    params.set('org_instance_id', serviceInstanceId)
  }
  const query = params.toString()
  return `${window.location.origin}/invoice/pay/${encodeURIComponent(token)}${query ? `?${query}` : ''}`
}

export default function FinanceInvoicesPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const currentReturnPath = getLocationReturnPath(location)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])

  const initialPage = Number(searchParams.get('page') || '1')
  const initialPageSize = Number(searchParams.get('page_size') || '20')
  const initialJobIdSearch = searchParams.get('job_id') || searchParams.get('search') || ''
  const initialPayStatusFilter = searchParams.get('pay_status')
  const normalizedInitialPayStatus: 'unpaid' | 'paid' | 'all_issued' =
    initialPayStatusFilter === 'unpaid'
    || initialPayStatusFilter === 'paid'
    || initialPayStatusFilter === 'all_issued'
      ? initialPayStatusFilter
      : 'all_issued'

  const [page, setPage] = useState(Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1)
  const [pageSize, setPageSize] = useState(Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 20)
  const [jobIdSearch, setJobIdSearch] = useState(initialJobIdSearch)
  const [payStatusFilter, setPayStatusFilter] = useState<'unpaid' | 'paid' | 'all_issued'>(normalizedInitialPayStatus)
  const [paymentLinkLoadingId, setPaymentLinkLoadingId] = useState<string | number | null>(null)
  const [complaintInvoiceTarget, setComplaintInvoiceTarget] = useState<{ id: string | number; label: string } | null>(null)
  const backPath = resolveReturnToPath(
    location.search,
    withServiceInstanceId(`/job/${initialJobIdSearch}`, serviceInstanceId),
  )

  const normalizedJobSearch = jobIdSearch.trim()
  const deferredNormalizedJobSearch = useDeferredValue(normalizedJobSearch)

  useEffect(() => {
    const params = new URLSearchParams()
    const returnTo = searchParams.get(RETURN_TO_PARAM)

    if (serviceInstanceId) params.set('service_instance_id', serviceInstanceId)
    if (returnTo) params.set(RETURN_TO_PARAM, returnTo)
    if (page > 1) params.set('page', String(page))
    if (pageSize !== 20) params.set('page_size', String(pageSize))
    if (normalizedJobSearch) params.set('job_id', normalizedJobSearch)
    if (payStatusFilter !== 'all_issued') params.set('pay_status', payStatusFilter)

    const nextSearch = params.toString()
    const currentSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search

    if (nextSearch !== currentSearch) {
      navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true })
    }
  }, [location.pathname, location.search, navigate, normalizedJobSearch, page, pageSize, payStatusFilter, searchParams, serviceInstanceId])

  const invoicesQuery = useQuery({
    queryKey: ['finance-invoices-list', serviceInstanceId ?? 'none', payStatusFilter],
    queryFn: () =>
      financeApi.getInvoices({
        page: 1,
        page_size: 5000,
        status: 'issued',
        pay_status: payStatusFilter === 'all_issued' ? undefined : payStatusFilter,
        service_instance_id: serviceInstanceId ?? undefined,
      }),
  })

  const resendInvoiceMutation = useMutation({
    mutationFn: (invoiceId: string | number) => financeApi.resendInvoice(invoiceId),
    onSuccess: () => {
      toast.success('Invoice resent successfully')
      queryClient.invalidateQueries({ queryKey: ['finance-invoices-list'] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const fetchedInvoices = invoicesQuery.data?.results ?? []
  const filteredInvoices = useMemo(() => {
    const issuedOnly = fetchedInvoices.filter((invoice) => invoice.status === 'issued')
    const searched = deferredNormalizedJobSearch
      ? issuedOnly.filter((invoice) => {
        const query = deferredNormalizedJobSearch.toLowerCase()
        const invoiceNumber = String(invoice.invoice_number || invoice.invoice_no || '').toLowerCase()
        const jobId = String(invoice.job ?? '').toLowerCase()
        return jobId.includes(query) || invoiceNumber.includes(query)
      })
      : issuedOnly

    if (payStatusFilter === 'unpaid') {
      return searched.filter((invoice) => invoice.paid_status !== 'paid')
    }

    if (payStatusFilter === 'paid') {
      return searched.filter((invoice) => invoice.paid_status === 'paid')
    }

    return searched
  }, [deferredNormalizedJobSearch, fetchedInvoices, payStatusFilter])

  const totalCount = filteredInvoices.length
  const invoices = useMemo(() => {
    const start = (page - 1) * pageSize
    return filteredInvoices.slice(start, start + pageSize)
  }, [filteredInvoices, page, pageSize])

  const unpaidCount = useMemo(
    () => filteredInvoices.filter((invoice) => invoice.paid_status !== 'paid').length,
    [filteredInvoices],
  )
  const unpaidPendingAmount = useMemo(
    () => filteredInvoices
      .filter((invoice) => invoice.paid_status !== 'paid')
      .reduce((sum, invoice) => sum + toNumber(invoice.payment_due), 0),
    [filteredInvoices],
  )

  const downloadInvoiceSummary = async (invoice: (typeof invoices)[number]) => {
    try {
      const [{ jsPDF }] = await Promise.all([
        import('jspdf'),
      ])

      const doc = new jsPDF()
      const invoiceNo = invoice.invoice_number || invoice.invoice_no || `INV-${invoice.id}`
      const invoiceDate = invoice.issued_at || invoice.created_at
      const amount = formatCurrency(toNumber(invoice.snapshot_grand_total))
      const balance = formatCurrency(toNumber(invoice.payment_due))
      const paidStatus = invoice.paid_status === 'paid' ? 'Paid' : 'Unpaid'

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(16)
      doc.text('Invoice Summary', 14, 18)

      doc.setFont('helvetica', 'normal')
      doc.setFontSize(11)
      doc.text(`Invoice No: ${invoiceNo}`, 14, 30)
      doc.text(`Job ID: ${invoice.job ?? '-'}`, 14, 38)
      doc.text(`Job Title: ${formatJobTitleWithId({
        jobId: invoice.job,
        title: invoice.job_title,
      })}`, 14, 46)
      doc.text(`Invoice Date: ${invoiceDate ? formatDate(invoiceDate) : '-'}`, 14, 54)
      doc.text(`Due Date: ${invoice.due_date ? formatDate(invoice.due_date) : '-'}`, 14, 62)
      doc.text(`Amount: ${amount}`, 14, 70)
      doc.text(`Balance: ${balance}`, 14, 78)
      doc.text(`Paid Status: ${paidStatus}`, 14, 86)

      const safeName = String(invoiceNo).replace(/[^a-zA-Z0-9-_]/g, '-')
      doc.save(`invoice-${safeName}.pdf`)
    } catch {
      toast.error('Unable to download invoice summary right now.')
    }
  }

  const resolveUpdatedPaymentLink = async (invoice: (typeof invoices)[number]) => {
    setPaymentLinkLoadingId(invoice.id)
    try {
      const freshInvoice = await financeApi.getInvoice(invoice.id, serviceInstanceId)
      const paymentUrl = buildCurrentPortalInvoicePaymentUrl(
        freshInvoice?.payment_url || invoice.payment_url,
        serviceInstanceId,
      )

      if (!paymentUrl) {
        throw new Error('Payment link is not available for this invoice.')
      }

      return paymentUrl
    } finally {
      setPaymentLinkLoadingId(null)
    }
  }

  const openPaymentLink = async (invoice: (typeof invoices)[number]) => {
    try {
      const paymentUrl = await resolveUpdatedPaymentLink(invoice)
      window.open(paymentUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to generate payment link')
    }
  }

  const copyPaymentLink = async (invoice: (typeof invoices)[number]) => {
    let paymentUrl = ''

    try {
      paymentUrl = await resolveUpdatedPaymentLink(invoice)
      await navigator.clipboard.writeText(paymentUrl)
      toast.success('Updated payment link copied')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to copy payment link')
    }
  }

  const getPaidStatus = (status: string) => (status === 'paid' ? 'Paid' : 'Unpaid')
  const getInvoiceStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Draft'
      case 'issued':
        return 'Issued'
      case 'paid':
        return 'Paid'
      case 'void':
        return 'Void'
      default:
        return status.replace(/_/g, ' ')
    }
  }

  const getInvoiceStatusClass = (status: string) => {
    switch (status) {
      case 'draft':
        return 'border-slate-200 bg-slate-50 text-slate-700'
      case 'issued':
        return 'border-blue-200 bg-blue-50 text-blue-700'
      case 'paid':
        return 'border-emerald-200 bg-emerald-50 text-emerald-700'
      case 'void':
        return 'border-rose-200 bg-rose-50 text-rose-700'
      default:
        return 'border-border bg-muted text-muted-foreground'
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              {initialJobIdSearch ? (
                <Button variant="ghost" size="icon" onClick={() => navigate(backPath)} title="Back">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              ) : null}
              <CardTitle>Customer Invoices</CardTitle>
            </div>
            <Button asChild variant="outline" size="sm" className="self-start sm:self-auto">
              <Link to={withServiceInstanceId('/finance/invoice-payments', serviceInstanceId)}>
                <CircleDollarSign className="mr-2 h-4 w-4" />
                Customer Payments
              </Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px_220px_220px]">
            <Input
              value={jobIdSearch}
              onChange={(event) => {
                setJobIdSearch(event.target.value)
                setPage(1)
              }}
              placeholder="Search by invoice number or Job ID"
            />
            <Select
              value={payStatusFilter}
              onValueChange={(value: 'unpaid' | 'paid' | 'all_issued') => {
                setPayStatusFilter(value)
                setPage(1)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pay Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_issued">All Issued</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            <div className="rounded-md border border-border/70 bg-muted/20 px-3 py-2">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Unpaid Count</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{unpaidCount}</p>
            </div>
            <div className="rounded-md border border-border/70 bg-muted/20 px-3 py-2">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Unpaid Pending Amount</p>
              <p className="mt-1 text-lg font-semibold text-foreground">{formatCurrency(unpaidPendingAmount)}</p>
            </div>
          </div>

          {invoicesQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
              No invoices found.
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-lg border border-border/70">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice Number</TableHead>
                      <TableHead>Job Id</TableHead>
                      <TableHead>Job Title</TableHead>
                      <TableHead>Invoice Date</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Invoice Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Paid Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => {
                      const invoiceDate = invoice.issued_at || invoice.created_at
                      const isPaymentLinkLoading = paymentLinkLoadingId === invoice.id
                      return (
                        <TableRow key={String(invoice.id)}>
                          <TableCell className="font-medium">{invoice.invoice_number || invoice.invoice_no || `INV-${invoice.id}`}</TableCell>
                          <TableCell>{invoice.job_ref ? `Job #${invoice.job_ref}` : String(invoice.job ?? '-')}</TableCell>
                          <TableCell className="font-medium">
                            <JobTitleLink
                              jobId={invoice.job}
                              title={invoice.job_title}
                              serviceInstanceId={serviceInstanceId}
                              returnPath={currentReturnPath}
                            />
                          </TableCell>
                          <TableCell>{invoiceDate ? formatDate(invoiceDate) : '-'}</TableCell>
                          <TableCell>{invoice.due_date ? formatDate(invoice.due_date) : '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getInvoiceStatusClass(invoice.status)}>
                              {getInvoiceStatusLabel(invoice.status)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(toNumber(invoice.snapshot_grand_total))}</TableCell>
                          <TableCell className="text-right">{formatCurrency(toNumber(invoice.payment_due))}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={invoice.paid_status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}>
                              {getPaidStatus(invoice.paid_status || 'unpaid')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Actions</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => {
                                    if (!invoice.job) return
                                    navigate(withReturnTo(withServiceInstanceId(`/finance/invoices/${invoice.job}`, serviceInstanceId), currentReturnPath))
                                  }}
                                  disabled={!invoice.job}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  View
                                </DropdownMenuItem>
                                {invoice.status === 'void' ? null : (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => navigate(withReturnTo(withServiceInstanceId(`/finance/invoices/${invoice.id}/payments`, serviceInstanceId), currentReturnPath))}
                                    >
                                      <CircleDollarSign className="mr-2 h-4 w-4" />
                                      Payments
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => void openPaymentLink(invoice)}
                                      disabled={isPaymentLinkLoading}
                                    >
                                      <ExternalLink className="mr-2 h-4 w-4" />
                                      {isPaymentLinkLoading ? 'Generating link...' : 'Open payment link'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => void copyPaymentLink(invoice)}
                                      disabled={isPaymentLinkLoading}
                                    >
                                      <Copy className="mr-2 h-4 w-4" />
                                      Copy updated payment link
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => resendInvoiceMutation.mutate(invoice.id)}
                                      disabled={resendInvoiceMutation.isPending}
                                    >
                                      <ExternalLink className="mr-2 h-4 w-4" />
                                      {resendInvoiceMutation.isPending ? 'Resending...' : 'Resend invoice'}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => void downloadInvoiceSummary(invoice)}
                                    >
                                      <Download className="mr-2 h-4 w-4" />
                                      Download
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => setComplaintInvoiceTarget({
                                        id: invoice.id,
                                        label: String(invoice.invoice_number || invoice.invoice_no || `INV-${invoice.id}`),
                                      })}
                                    >
                                      <AlertTriangle className="mr-2 h-4 w-4" />
                                      Complaint conversation
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {totalCount > 0 ? (
                <div className="mt-4">
                  <DataTablePagination
                    page={page}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    onPageChange={setPage}
                    onPageSizeChange={(value) => {
                      setPageSize(value)
                      setPage(1)
                    }}
                  />
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={complaintInvoiceTarget !== null} onOpenChange={(open) => { if (!open) setComplaintInvoiceTarget(null) }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[1100px]">
          <DialogHeader>
            <DialogTitle>Invoice Complaint Conversation</DialogTitle>
            <DialogDescription>
              {complaintInvoiceTarget ? `Invoice ${complaintInvoiceTarget.label}` : 'Manage complaint conversation for this invoice.'}
            </DialogDescription>
          </DialogHeader>
          {complaintInvoiceTarget ? <ComplaintsPanel invoiceId={complaintInvoiceTarget.id} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}